import React, { useContext, useEffect, useState } from 'react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import useTableRowSelection from '../../../../../../../../components/Table/ReusableCells/AllRowsSelectionCell/useTableRowSelection'
import { useLoadPaginatedList } from '../../../../../../../../features/hooks/useLoadPaginatedList'
import useProductsFilters from './useProductsFilters'

import { MediaOrderFormContext } from '../../../../../MediaOrderFormContext'

import { clearProducts, getProducts } from '../../../../../../../../modules/actions/mediaOrdersProducts'
import {
  selectedControllerDataSelector,
  selectedControllerIdSelector
} from '../../../../../../../../modules/selectors/app'
import { productsNextSelector } from '../../../../../../../../modules/selectors/mediaOrdersProducts'

import { PRODUCT_PERIODS_DATES } from '../../../../fields'
import { initialDatePeriod } from '../../../../../../../../constants/dates'

export const AddProductsBulkContext = React.createContext()

export function AddProductsBulkDataProvider({
  children,
  lastAddedProduct,
  addedProductsIds,
  onFiltersChange,
  selectedValidProducts
}) {
  const selectedController = useSelector(selectedControllerDataSelector)
  const allowPastPeriods = selectedController?.allow_create_past_proposals

  const [dateRange, setDateRange] = useState(() => {
    if (lastAddedProduct) {
      // if the proposal has at least one product already, prepopulate this field with the dates of the last media product on the proposal.
      const dates = lastAddedProduct[PRODUCT_PERIODS_DATES]

      return allowPastPeriods || (dates && new Date(dates[0].date_start) >= new Date())
        ? {
            startDate: new Date(dates[0].date_start),
            endDate: new Date(dates[dates.length - 1].date_end)
          }
        : // when past periods are not allowed, we need to set the date range to the initial date period
          initialDatePeriod
    } else {
      // If there are no products yet, don't select date and don't fetch inventory
      return initialDatePeriod
    }
  })
  const hasDateRangeSelected = useMemo(() => {
    return !!(dateRange.startDate && dateRange.endDate)
  }, [dateRange])

  const [hideUnavailable, setHideUnavailable] = useState(false)

  const controllerId = useSelector(selectedControllerIdSelector)
  const productsNext = useSelector(productsNextSelector)

  const { currency } = useContext(MediaOrderFormContext)
  const { rowIds, rowSelection, rowSelectionList, setRowSelection } = useTableRowSelection(selectedValidProducts)

  const currencyCode = currency?.code
  const params = useMemo(
    () => ({
      ...(addedProductsIds && { exclude_ids: addedProductsIds.join(',') }),
      ordering: 'name',
      controller: controllerId,
      in_stock: true,
      currency_code: currencyCode,
      status: 'active' // filter by active only(but currently for amendments we will fetch all)
    }),
    [addedProductsIds, controllerId, currencyCode]
  )

  const { filtersState, setFiltersState, requestParams } = useProductsFilters(params)

  const loadMoreProductsHandler = useLoadPaginatedList({
    allowFetch: hasDateRangeSelected,
    params: requestParams,
    action: getProducts,
    clearAction: clearProducts,
    next: productsNext
  })

  useEffect(() => {
    onFiltersChange && onFiltersChange(requestParams)
  }, [onFiltersChange, requestParams])

  const mediaProductsContextProps = useMemo(() => {
    return {
      dateRange,
      setDateRange,
      hasDateRangeSelected,
      filtersState,
      setFiltersState,
      hideUnavailable,
      setHideUnavailable,
      isAllProductsLoaded: !productsNext,
      loadMoreProductsHandler,
      rowIds,
      rowSelection,
      rowSelectionList,
      setRowSelection
    }
  }, [
    dateRange,
    hasDateRangeSelected,
    filtersState,
    setFiltersState,
    hideUnavailable,
    productsNext,
    loadMoreProductsHandler,
    rowIds,
    rowSelection,
    rowSelectionList,
    setRowSelection
  ])

  return <AddProductsBulkContext.Provider value={mediaProductsContextProps}>{children}</AddProductsBulkContext.Provider>
}
